import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, finalize, Subject, switchMap, takeUntil } from 'rxjs';
import { GoogleAutocompleteComponent } from 'src/app/module/google-map/component/google-autocomplete/google-autocomplete.component';
import { AddCustomPropertyComponent } from 'src/app/module/order/component/add-custom-property/add-custom-property.component';
import { AddNewCustomSubjectParams } from 'src/app/module/order/interface/order.interface';
import { OrderService } from 'src/app/module/order/service/order.service';
import { OrderState } from 'src/app/module/order/state/order.state';
import { ProfetControlWidgetModule } from 'src/app/module/profet-control-widget/profet-control-widget.module';
import { NoContentBannerComponent } from 'src/app/shared/components/no-content-banner/no-content-banner.component';
import { ProgressLoaderService } from 'src/app/shared/services/progress-loader.service';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  NewOrder,
  PropertySearchCriteria,
  SearchedProperty,
  SearchedPropertyData,
  SearchedPropertyEmitData,
  SearchMode,
  SearchPropertyQuery,
} from '../../interface/new-order.interface';
import { NewOrderService } from '../../service/new-order.service';
import { NewOrderState } from '../../state/new-order.state';
import { PropertyDescriptionComponent } from '../property-description/property-description.component';
import { PropertyTableComponent } from '../property-table/property-table.component';

@Component({
  selector: 'app-match-subject',
  standalone: true,
  imports: [
    CommonModule,
    ProfetControlWidgetModule,
    PropertyTableComponent,
    FormsModule,
    ReactiveFormsModule,
    NoContentBannerComponent,
    AddCustomPropertyComponent,
    PropertyDescriptionComponent,
    MatCardModule,
    MatIconModule,
    SharedModule,
    GoogleAutocompleteComponent,
    MatButtonModule,
  ],
  templateUrl: './match-subject.component.html',
  styleUrl: './match-subject.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MatchSubjectComponent {
  subjectProperty?: SearchedProperty | null;
  searchedPropertyList: Array<SearchedProperty> = [];
  searchCount!: number;
  parseFloat = parseFloat;
  openCustomPropertyForm: boolean = false;
  searchPropertyParam: SearchPropertyQuery | null = null;
  hasDistanceError: boolean = false;
  searchAttempted: boolean = false;
  actualDistanceFt: number = 0;
  onDestroyNotifier$ = new Subject<void>();

  private _draftResolvedProperty?: SearchedProperty | null;

  constructor(
    private _cdr: ChangeDetectorRef,
    private newOrderService: NewOrderService,
    public _route: ActivatedRoute,
    public _router: Router,
    public newOrderState: NewOrderState,
    public orderState: OrderState,
    private orderService: OrderService,
    private progressLoaderService: ProgressLoaderService
  ) {}

  ngOnInit() {
    const streetAddress = this.orderState.orderDetail?.streetAddress ?? '';
    const streetNumberMatch = streetAddress?.match(/^\d+/) ?? null;
    const streetNumber = streetNumberMatch ? streetNumberMatch[0] : null;
    const streetName = streetAddress?.replace(streetNumber ?? '', '').trim() ?? null;

    this.searchPropertyParam = {
      searchMode: 'address',
      searchData: {
        address: this.orderState.orderDetail.orderAddress ?? '',
        streetNumber: streetNumber ?? '',
        streetName: streetName ?? '',
        city: this.orderState.orderDetail.city ?? '',
        state: this.orderState.orderDetail.state ?? '',
        zip: this.orderState.orderDetail.zip ?? '',
      },
    };

    this.initialPropertySearch();
  }

  onPropertySearch(data: SearchedPropertyEmitData) {
    this.searchedPropertyList = data.property.propertyList ?? [];
    this.searchCount = data.property.count ?? 0;
    this.subjectProperty = null;
    this.hasDistanceError = false;
    this.searchAttempted = true;
    if (this.searchedPropertyList?.length == 1) {
      this.subjectProperty = this.searchedPropertyList[0];
      this._validateDistance();
    }
    this._cdr.detectChanges();
  }

  initialPropertySearch() {
    const loaderId = this.progressLoaderService.show('Searching for Property');

    const reqParams: PropertySearchCriteria = {
      StreetNumber: this.searchPropertyParam?.searchData?.streetNumber ?? '',
      StreetName: this.searchPropertyParam?.searchData?.streetName
        ? this.searchPropertyParam?.searchData?.unitNumber
          ? `${this.searchPropertyParam?.searchData?.streetName} #${this.searchPropertyParam?.searchData?.unitNumber}`
          : this.searchPropertyParam?.searchData?.streetName
        : '',
      City: this.searchPropertyParam?.searchData?.city ?? '',
      State: this.searchPropertyParam?.searchData?.state ?? '',
      zip: this.searchPropertyParam?.searchData?.zip ?? '',
      fipscode: this.searchPropertyParam?.searchData?.fipsCode ?? '',
      pagenumber: 1,
      pagesize: 10,
      AdditionalSources: 'Assessor,HUDLISTING',
    };

    this.newOrderService
      .searchProperty(reqParams, this.searchPropertyParam?.searchMode as SearchMode)
      .pipe(
        takeUntil(this.onDestroyNotifier$),
        finalize(() => this.progressLoaderService.hide(loaderId))
      )
      .subscribe((res: SearchedPropertyData) => {
        this._draftResolvedProperty = res.propertyList?.length === 1 ? res.propertyList[0] : null;
        this.onPropertySearch({ property: res });
      });
  }

  onPropertySelection(property: SearchedProperty): void {
    this.subjectProperty = property;
    this._validateDistance();
    this._cdr.detectChanges();
  }

  triggerCustomPropertyForm() {
    this.openCustomPropertyForm = true;
    this.subjectProperty = null;
  }

  onConfirm() {
    const reqParam = {
      ListingId: this.subjectProperty?.ListingId,
      PMXPropertyId: this.subjectProperty?.PMXPropertyId,
      id: this.orderState.orderDetail.id,
      subjectAddressChangedFromDraft: !this._isSameResolvedProperty(this.subjectProperty, this._draftResolvedProperty),
    };
    this.createOrder(reqParam);
  }

  continueWithUnmatchedProperty() {
    const detail = this.orderState.orderDetail;
    this.addSubjectManually(
      {
        userRecord: {
          propertyStreetAddress: detail.streetAddress ?? '',
          propertyCity: detail.city ?? '',
          propertyState: detail.state ?? '',
          propertyZip: detail.zip ?? '',
        },
      },
      false
    );
  }

  addSubjectManually(data: AddNewCustomSubjectParams, addressChangedFromDraft: boolean = true) {
    const reqParam = {
      property: {
        ...data.userRecord,
        UnparsedAddress: data.userRecord.propertyStreetAddress
          ? data.userRecord.propertyStreetAddress
          : this.orderService.getUnparsedAddress(data.userRecord),
        City: data.userRecord.propertyCity,
        PostalCode: data.userRecord.propertyZip,
        StateOrProvince: data.userRecord.propertyState,
      },
      id: this.orderState.orderDetail.id,
      subjectAddressChangedFromDraft: addressChangedFromDraft,
    };
    this.createOrder(reqParam);
  }

  createOrder(param: NewOrder) {
    // The draft order being confirmed is the source of truth here - the new order wizard state is not
    // populated on this route, which is only reached by resuming an existing draft.
    this.newOrderService
      .confirmBillingEligibility(this.orderState.orderDetail)
      .pipe(
        filter((confirmation) => confirmation.proceed),
        switchMap((confirmation) => this.newOrderService.createOrder(param, confirmation.companyId)),
        takeUntil(this.onDestroyNotifier$)
      )
      .subscribe(() => {
        this._cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }

  // Two properties match if they share any non-empty identifier.
  private _isSameResolvedProperty(final?: SearchedProperty | null, draft?: SearchedProperty | null): boolean {
    if (!final || !draft) {
      return false;
    }

    const identityKeys: Array<'PMXPropertyId' | 'ListingId' | 'ParcelNumber'> = [
      'PMXPropertyId',
      'ListingId',
      'ParcelNumber',
    ];

    return identityKeys.some((key) => {
      const finalValue = (final[key] ?? '').trim();
      const draftValue = (draft[key] ?? '').trim();
      return !!finalValue && finalValue === draftValue;
    });
  }

  private _validateDistance() {
    const tolerance = Number(this.orderState.orderSetting?.subjectCoordinateTolerance ?? 0);
    const hasCoordinates =
      this.subjectProperty?.Latitude &&
      this.subjectProperty?.Longitude &&
      this.orderState.orderDetail?.draftInfo?.latitude &&
      this.orderState.orderDetail?.draftInfo?.longitude;

    if (!tolerance || !hasCoordinates) {
      this.hasDistanceError = false;
      return;
    }

    const distance = this.orderService.calculateDistance(
      {
        lat: this.subjectProperty?.Latitude,
        lng: this.subjectProperty?.Longitude,
      },
      {
        lat: this.orderState.orderDetail?.draftInfo?.latitude,
        lng: this.orderState.orderDetail?.draftInfo?.longitude,
      }
    );

    this.actualDistanceFt = Math.round(Number(distance) * 5280);
    this.hasDistanceError = Number(distance) > tolerance / 5280;
  }
}
